import { Button, ExpansionList, ExpansionPanel, FontIcon } from 'react-md'
import { invoicingMethodMessage } from 'ui/ManagerFinance/utils/messages'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'
import { useFormContext, useWatch } from 'react-hook-form'
import type { MeterInvoicingForm } from './types'
import { useMemo, useState } from 'react'

import type {
	ContractKey,
	ContractSort,
} from 'ui/ManagerFinance/Contracts/types'
import LoadingState from 'ui/components/LoadingState'
import ContractDialog from 'ui/ManagerFinance/Contracts/ContractDialog'
import ContractTable from 'ui/ManagerFinance/Contracts/ContractTable'
import { InvoicingMethod } from 'ui/ManagerFinance/enum'
import useParties from 'ui/ManagerFinance/Parties/useParties'
import type { SelectOptions } from 'ui/ManagerFinance/types'
import { createApartmentOptions } from '@viilu-web/shared/util/apartmentOptions'
import { createPartyOptions } from 'ui/ManagerFinance/utils/partyOptions'
import useContracts from 'ui/ManagerFinance/Contracts/useContracts'
import type { Apartment } from 'types/apartment'
import type { Building } from 'types/building'

const AddContractButton = styled(Button)`
	display: flex;
	flex-flow: row nowrap;
	align-items: center;
	justify-content: center;
	margin-top: 0.5rem;
	gap: 0.5rem;
`

const CreateContractContainer = styled.div`
	display: flex;
	flex-flow: column nowrap;
	gap: 1rem;
	margin-top: 1rem;
	margin-bottom: 2rem;
`

const IconCircleContainer = styled.span<{ $backgroundColor: string }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	background-color: ${(props) => props.$backgroundColor};
`
type Props = {
	companyOptions: SelectOptions[]
	buildings: Building[]
	companyApartments: Apartment[]
}

const ContractsForm = ({
	companyOptions,
	buildings,
	companyApartments,
}: Props) => {
	const intl = useIntl()

	const { setValue } = useFormContext<MeterInvoicingForm>()

	//TODO: Add contract filtering based on start-end dating
	const [companyUUID, paymentProductUUIDs, contractUUIDs = []] = useWatch({
		name: ['companyUUID', 'paymentProductUUIDs', 'contractUUIDs'],
	})

	const [editingContract, setEditingContract] = useState<
		| {
				contractUUID: string
				apartmentUUID: string
				apartmentIdentifier: string
		  }
		| undefined
	>(undefined)

	const [addContract, setAddContract] = useState<
		{ apartmentUUID: string; apartmentIdentifier: string } | undefined
	>(undefined)

	const [sort, setSort] = useState<ContractSort>({
		key: 'apartmentUUID',
		type: 'apartmentOptions',
		order: 'asc',
	})

	const partyFilters = useMemo(
		() => ({
			reviewDate: new Date(),
			active: true,
			bondName: '',
			apartmentUUID: '',
			invoicingMethod: '',
		}),
		[],
	)

	const { parties, refreshParties } = useParties(companyUUID, partyFilters)
	const payerOptions = createPartyOptions(parties, intl)
	const apartmentOptions = createApartmentOptions(companyApartments, buildings)
	const invoicingOptions: SelectOptions[] = Object.values(InvoicingMethod).map(
		(value) => ({
			label: invoicingMethodMessage(intl, value),
			value: value,
		}),
	)

	const filters = useMemo(() => {
		if (!companyApartments || companyApartments.length === 0)
			return { companyUUID, paymentProductUUIDs }

		return {
			companyUUID,
			paymentProductUUIDs,
			apartmentUUIDs: companyApartments.map((apt) => apt.uuid),
		}
	}, [companyUUID, paymentProductUUIDs, companyApartments])

	const _useContracts = useContracts(filters)
	const { contracts, loadingContracts } = _useContracts

	const changeSort = (key: ContractKey, type?: string) => {
		if (sort.key === key) {
			setSort((prevData) => ({
				...prevData,
				order: sort.order === 'asc' ? 'desc' : 'asc',
			}))
		} else {
			setSort({
				key: key,
				type: type || null,
				order: 'asc',
			})
		}
	}

	const loading = loadingContracts

	return (
		<CreateContractContainer>
			{loading && <LoadingState />}
			{!loading && (
				<ExpansionList style={{ marginBottom: '2rem' }}>
					{companyApartments.map(
						({ uuid: apartmentUUID, identifier: apartmentIdentifier }) => {
							const contractsForApartment = contracts.filter(
								(contract) => contract.apartmentUUID === apartmentUUID,
							)
							const contract = contractsForApartment.find((c) =>
								contractUUIDs.includes(c.uuid),
							)
							const status = contract?.uuid
								? 'ready'
								: contractsForApartment.length > 0
								? 'needs_action'
								: 'needs_contract'

							return (
								<ExpansionPanel
									style={{ padding: '0 1rem 0 1rem' }}
									key={`contract-list-panel-${apartmentIdentifier}`}
									footer={null}
									defaultExpanded={!!companyApartments.length}
									label={
										<div
											style={{
												display: 'flex',
												flexFlow: 'row',
												alignItems: 'center',
												gap: '1rem',
											}}
										>
											{status === 'needs_contract' && (
												<IconCircleContainer $backgroundColor="var(--color-error)">
													<FontIcon
														forceFontSize
														forceSize={20}
														style={{ color: 'white' }}
													>
														close
													</FontIcon>
												</IconCircleContainer>
											)}
											{status === 'needs_action' && (
												<IconCircleContainer $backgroundColor="var(--gray-6)">
													<FontIcon
														forceFontSize
														forceSize={20}
														style={{ color: 'white' }}
													>
														priority_high
													</FontIcon>
												</IconCircleContainer>
											)}
											{status === 'ready' && (
												<IconCircleContainer $backgroundColor="var(--color-success)">
													<FontIcon
														forceFontSize
														forceSize={20}
														style={{ color: 'white' }}
													>
														done
													</FontIcon>
												</IconCircleContainer>
											)}
											<div>
												<p>{apartmentIdentifier}</p>
												<div
													style={{
														display: 'flex',
														flexFlow: 'row nowrap',
														gap: '0.25rem',
													}}
												>
													{contractsForApartment.length === 0 && (
														<FormattedMessage
															defaultMessage="Sopimuksia ei löytynyt huoneistolle."
															description="No contracts were found for apartment"
														/>
													)}
													{contractsForApartment.length > 0 && (
														<span>
															<FormattedMessage
																defaultMessage="Sopimuksia löytyi huoneistolle {contractAmount} kappaletta."
																description="Found {contractAmount} contracts for the apartment"
																values={{
																	contractAmount: contractsForApartment.length,
																}}
															/>
														</span>
													)}
													{status === 'needs_action' && (
														<span>
															<FormattedMessage
																defaultMessage="Valitse sopimus."
																description="Select contract"
															/>
														</span>
													)}
													{status === 'needs_contract' && (
														<span>
															<FormattedMessage
																defaultMessage="Luo uusi sopimus huoneistolle."
																description="Create new contract for the apartment"
															/>
														</span>
													)}
												</div>
											</div>
										</div>
									}
								>
									<ContractTable
										key={`${apartmentUUID}-${apartmentIdentifier}`}
										id={`${apartmentUUID}-${apartmentIdentifier}`}
										contracts={contractsForApartment}
										onClickContract={(contractUUID) =>
											setEditingContract({
												contractUUID,
												apartmentUUID: apartmentUUID,
												apartmentIdentifier: apartmentIdentifier,
											})
										}
										sort={sort}
										changeSort={changeSort}
										apartmentOptions={apartmentOptions}
										companyOptions={companyOptions}
										processing={false}
										selectedContractUUID={contract?.uuid}
										onContractSelected={(uuid) => {
											const isSelected = contractUUIDs.some((c) => c === uuid)

											if (isSelected) {
												setValue(
													'contractUUIDs',
													contractUUIDs.filter((c) => c !== uuid),
												)
											} else {
												const selectedContract = contractsForApartment.find(
													(c) => c.uuid === uuid,
												)
												if (!selectedContract) return

												let updatedUUIDs = [...contractUUIDs]

												if (contract) {
													updatedUUIDs = updatedUUIDs.filter(
														(id) => id !== contract.uuid,
													)
												}
												updatedUUIDs.push(selectedContract.uuid)

												setValue('contractUUIDs', updatedUUIDs)
											}
										}}
									/>
									{status !== 'ready' && (
										<div>
											<AddContractButton
												secondary
												flat
												onClick={() =>
													setAddContract({
														apartmentUUID: apartmentUUID,
														apartmentIdentifier: apartmentIdentifier,
													})
												}
											>
												<FontIcon
													style={{
														color: 'var(--color-secondary-dark)',
													}}
												>
													add
												</FontIcon>
												<FormattedMessage
													defaultMessage="Lisää uusi sopimus"
													description="Button label for adding new contract"
												/>
											</AddContractButton>
										</div>
									)}
								</ExpansionPanel>
							)
						},
					)}
				</ExpansionList>
			)}

			{addContract && (
				<ContractDialog
					useContracts={_useContracts}
					onHide={() => setAddContract(undefined)}
					selectedFinanceCompanyUUID={companyUUID}
					selectedApartment={{
						uuid: addContract.apartmentUUID,
						identifier: addContract.apartmentIdentifier,
					}}
					companyOptions={companyOptions}
					payerOptions={payerOptions}
					apartmentOptions={apartmentOptions.filter(
						(option) => option.value === addContract.apartmentUUID,
					)}
					invoicingOptions={invoicingOptions}
					companyApartments={companyApartments}
					refreshParties={refreshParties}
					onlyShareholders
				/>
			)}
			{editingContract && (
				<ContractDialog
					id={editingContract.contractUUID}
					useContracts={_useContracts}
					onHide={() => setEditingContract(undefined)}
					selectedFinanceCompanyUUID={companyUUID}
					selectedApartment={{
						uuid: editingContract.apartmentUUID,
						identifier: editingContract.apartmentIdentifier,
					}}
					companyOptions={companyOptions}
					payerOptions={payerOptions}
					apartmentOptions={apartmentOptions.filter(
						(option) => option.value === editingContract.apartmentUUID,
					)}
					invoicingOptions={invoicingOptions}
					companyApartments={companyApartments}
					refreshParties={refreshParties}
					onlyShareholders
				/>
			)}
			<p>
				<FormattedMessage
					defaultMessage="Sopimuksia on määritelty {definedContracts} / {maxContracts}."
					description="Contracts have been defined for {definedContracts} / {maxContracts}."
					values={{
						definedContracts: contractUUIDs.length,
						maxContracts: companyApartments.length,
					}}
				/>
			</p>
		</CreateContractContainer>
	)
}
export default ContractsForm
